import * as React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Heading, Paragraph, Subheading } from 'components/src/typography';
import { formatLink } from '@/utils/utils';
import { AnswerFaqSectionFieldsFragment, QuestionFaqSectionFieldsFragment } from 'types/generated';
import Image from 'next/image';
import { getOr } from '@/utils/options';

type Props = {
  data: AnswerFaqSectionFieldsFragment | QuestionFaqSectionFieldsFragment;
};

const getEntry = (data: AnswerFaqSectionFieldsFragment, id: string) => {
  if (!data.links.entries.block) {
    return;
  }

  return data.links.entries.block.find((x) => x?.sys.id === id);
};

const RichTextContainer = ({ data }: Props) => {
  const textColor = React.useMemo(() => {
    return data.__typename === 'QAndAQuestion' ? 'text-black-50' : 'text-neutral-10';
  }, [data.__typename]);

  return (
    <>
      {documentToReactComponents(data.json as Document, {
        renderNode: {
          [BLOCKS.HEADING_6]: (_node, children) => <Subheading classes={textColor}>{children}</Subheading>,
          [BLOCKS.HEADING_5]: (_node, children) => (
            <Heading classes={textColor} variant="xs">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_4]: (_node, children) => (
            <Heading classes={textColor} variant="small">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_3]: (_node, children) => (
            <Heading classes={textColor} variant="medium">
              {children}
            </Heading>
          ),
          [BLOCKS.UL_LIST]: (_node, children) => <ul className={`flex list-disc flex-col gap-2`}>{children}</ul>,
          [BLOCKS.HEADING_2]: (_node, children) => (
            <Heading classes={textColor} variant="large">
              {children}
            </Heading>
          ),
          [BLOCKS.HEADING_1]: (_node, children) => (
            <Heading classes={textColor} variant="xl">
              {children}
            </Heading>
          ),
          [BLOCKS.PARAGRAPH]: (_node, children) => {
            return <Paragraph classes={`whitespace-break-spaces empty:h-24 ${textColor}`}>{children}</Paragraph>;
          },
          [INLINES.HYPERLINK]: (node, children) => {
            return (
              <a
                href={formatLink(node.data.uri)}
                className="body-lg text-red-75 hover:underline hover:decoration-red-100"
              >
                {children}
              </a>
            );
          },
          [BLOCKS.EMBEDDED_ENTRY]: (node) => {
            const entry = (data.__typename === 'QAndAAnswer' && getEntry(data, node.data.target.sys.id)) || undefined;
            if (entry?.__typename === 'ImageWithFocalPoint') {
              return (
                <Image
                  src={getOr(entry.image?.url, '')}
                  alt=""
                  width="0"
                  height="0"
                  sizes={'800px'}
                  className="h-auto w-full rounded-28"
                />
              );
            }
            return null;
          },
        },
      })}
    </>
  );
};
export default RichTextContainer;
